import { FormInstance } from "sunday-component/node_modules/antd";
import { some } from "lodash";
import { DATE_INPUT_TEXT_FORMAT } from "src/constants/format";
import { isMobile } from "src/utils/deviceDetector";
import dayjs, { Dayjs } from "dayjs";
import {
  DATE_PICKER_DATE_FORMAT,
  formatThaiBuddhistDate,
  isAfterDate,
  isBeforeDate,
  isToday,
} from "../../../utils/date";
import {
  CustomDatePicker,
  DatePickerGlobalStyle,
  DisplayValue,
  DisplayValueContainer,
} from "./DatePicker.styled";

export interface DatePickerInputProp {
  value?: Date | Dayjs | string;
  form?: FormInstance;
  dataTestId?: string;
  format?: string;
  placeholder?: string;
  disabledPast?: boolean;
  disabledToday?: boolean;
  disabledFuture?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  onChange?: (value: Dayjs) => void;
  disabledDate?: (value: Date) => boolean;
  defaultPickerValue?: Date | Dayjs | string;
  className?: string;
  classNameInput?: string;
  useThaiBuddhistDateDisplay?: boolean;
  style?: object;
  showBuddhistEra?: boolean;
  displayValue?: string;
}

export const defaultDisabledDate =
  ({ disabledPast, disabledToday, disabledFuture }) =>
  (date) =>
    some([
      disabledPast && isBeforeDate(date),
      disabledToday && isToday(date),
      disabledFuture && isAfterDate(date),
    ]);

const DatePicker = ({
  value,
  form,
  format = DATE_PICKER_DATE_FORMAT,
  dataTestId,
  placeholder,
  disabledPast,
  disabledToday,
  disabledFuture,
  disabled = false,
  allowClear = false,
  onChange,
  disabledDate,
  defaultPickerValue,
  className,
  classNameInput,
  useThaiBuddhistDateDisplay = false,
  showBuddhistEra = true,
  displayValue,
  ...props
}: DatePickerInputProp) => {
  const openToValue = value || defaultPickerValue;
  const parsedDate = dayjs(openToValue);
  const hasThaiBuddhistDisplayValue =
    useThaiBuddhistDateDisplay && parsedDate.isValid();
  const picker = (
    <CustomDatePicker
      value={
        hasThaiBuddhistDisplayValue
          ? formatThaiBuddhistDate(parsedDate)
          : (openToValue as any as string)
      }
      {...(hasThaiBuddhistDisplayValue
        ? ({ selected: parsedDate.toDate() } as any)
        : {})}
      {...(openToValue
        ? ({ openToDate: dayjs(openToValue).toDate() } as any)
        : {})}
      data-testid={dataTestId}
      data-gtm-input-code={dataTestId}
      format={format}
      placeholder={placeholder}
      disabled={disabled}
      filterDates={
        disabledDate
          ? (v) => !disabledDate(v)
          : (v) =>
              !defaultDisabledDate({
                disabledPast,
                disabledToday,
                disabledFuture,
              })(v)
      }
      showBuddhistEra={showBuddhistEra}
      onChange={(v) => onChange(dayjs(v, DATE_INPUT_TEXT_FORMAT))}
      className={className}
      classNameInput={classNameInput}
      {...{ autocomplete: "off" }}
      {...(isMobile ? { onFocus: (e) => e.target.blur() } : {})}
      {...(displayValue
        ? {
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (
                e.key.length === 1 ||
                e.key === "Backspace" ||
                e.key === "Delete"
              ) {
                e.preventDefault();
              }
            },
          }
        : {})}
      {...props}
    />
  );

  return (
    <>
      <DatePickerGlobalStyle />
      {displayValue ? (
        <DisplayValueContainer>
          {picker}
          <DisplayValue $disabled={disabled}>{displayValue}</DisplayValue>
        </DisplayValueContainer>
      ) : (
        picker
      )}
    </>
  );
};

export default DatePicker;
